import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { FieldVisibilitySettings } from '@/components/settings/FieldVisibilitySettings';
import {
  fetchUsersWithPermissions,
  fetchAdminStats,
  fetchTableInfo,
  deleteUser,
} from '@/services/admin';
import type { UserWithPermissions, AdminStats, TableInfo, SortState, SearchState } from '@/types/admin';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { UserEditModal } from './UserEditModal';
import { Plus, Search, Users, Shield, Table, Trash2, RefreshCw, Settings } from 'lucide-react';
import { cn } from '@/utils';

export function AdminPage() {
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    adminCount: 0,
    managerCount: 0,
    userCount: 0,
    totalTables: 0,
  });
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchState, setSearchState] = useState<SearchState>({ query: '' });
  const [sortState] = useState<SortState>({ column: 'created_at', direction: 'desc' });
  const [selectedUser, setSelectedUser] = useState<UserWithPermissions | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithPermissions | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, statsData, tablesData] = await Promise.all([
        fetchUsersWithPermissions(),
        fetchAdminStats(),
        fetchTableInfo(),
      ]);
      setUsers(usersData);
      setStats(statsData);
      setTables(tablesData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter users based on search
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !searchState.query ||
      user.email?.toLowerCase().includes(searchState.query.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchState.query.toLowerCase());

    const matchesRole = !searchState.role || user.role === searchState.role;

    return matchesSearch && matchesRole;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aVal = a[sortState.column as keyof UserWithPermissions] ?? '';
    const bVal = b[sortState.column as keyof UserWithPermissions] ?? '';

    if (aVal < bVal) return sortState.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortState.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle user edit
  const handleEditUser = (user: UserWithPermissions) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  // Handle user deletion
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);
      await loadData();
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Handle user save (update permissions)
  const handleSaveUser = async () => {
    await loadData();
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get role label
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return t('role.admin') || 'Admin';
      case 'manager':
        return t('role.manager') || 'Manager';
      default:
        return t('role.user') || 'User';
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">
          You don't have permission to access the admin panel.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.userManagement') || 'User Management'}</h1>
          <p className="text-muted-foreground">
            {t('admin.manageUsers') || 'Manage users, roles, and table permissions'}
          </p>
        </div>
        <Button onClick={() => { setSelectedUser(null); setEditModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.addUser') || 'Add User'}
        </Button>
        <Button variant="outline" onClick={() => setSettingsDialogOpen(true)} className="gap-2">
          <Settings className="w-4 h-4" />
          Fields
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('admin.totalUsers') || 'Total Users'}</p>
              <p className="text-2xl font-bold">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('admin.admins') || 'Admins'}</p>
              <p className="text-2xl font-bold">{stats.adminCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('admin.managers') || 'Managers'}</p>
              <p className="text-2xl font-bold">{stats.managerCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Table className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('admin.tables') || 'Tables'}</p>
              <p className="text-2xl font-bold">{stats.totalTables}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('admin.searchUsers') || 'Search users...'}
            value={searchState.query}
            onChange={(e) => setSearchState((prev) => ({ ...prev, query: e.target.value }))}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
          {t('common.refresh') || 'Refresh'}
        </Button>
      </div>

      {/* Users Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-4 font-medium">{t('admin.user') || 'User'}</th>
                <th className="text-left p-4 font-medium">{t('admin.role') || 'Role'}</th>
                <th className="text-left p-4 font-medium">{t('admin.tables') || 'Tables'}</th>
                <th className="text-left p-4 font-medium">{t('admin.created') || 'Created'}</th>
                <th className="text-right p-4 font-medium">{t('admin.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>{t('common.loading') || 'Loading...'}</span>
                    </div>
                  </td>
                </tr>
              ) : sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    {t('admin.noUsers') || 'No users found'}
                  </td>
                </tr>
              ) : (
                sortedUsers.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-muted/25">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {user.full_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{user.full_name || 'Unnamed'}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant={getRoleBadgeVariant(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <span className="text-sm">
                          {user.permissions?.length || 0} {t('admin.tables') || 'tables'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditUser(user)}>
                          {t('common.edit') || 'Edit'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setUserToDelete(user);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create User Modal */}
      {editModalOpen && selectedUser !== undefined && (
        <UserEditModal
          user={selectedUser}
          tables={tables}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedUser(null);
          }}
          onSave={handleSaveUser}
        />
      )}

      {/* Field Visibility Settings Dialog */}
      <FieldVisibilitySettings
        isOpen={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 flex items-center justify-center z-50',
          !deleteConfirmOpen && 'hidden'
        )}
        onClick={() => setDeleteConfirmOpen(false)}
      >
        <div
          className="bg-card rounded-xl p-6 max-w-sm sm:max-w-md w-[90vw] sm:w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold mb-2">{t('admin.deleteUser') || 'Delete User'}</h3>
          <p className="text-muted-foreground mb-6">
            {t('admin.deleteUserConfirm') ||
              `Are you sure you want to delete ${userToDelete?.full_name || userToDelete?.email}? This action cannot be undone.`}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              {t('common.delete') || 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
